import React, { useMemo, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tabs,
    Tab,
    Radio,
    FormControlLabel,
    RadioGroup,
    Typography,
    Grid,
    Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import dayjs from 'dayjs';
import { dayjsToDateTimeString } from '../../../utils';
import CustomDateTimePicker from '../../common/CustomDateTimePicker';
import { ASSET_TYPE_CAR, ASSET_TYPE_PLACE } from '../../../utils/constants';
import { Box, padding, useTheme } from '@mui/system';
import CustomTable from '../../common/CustomTable';

const FilterRadioButton = ({ value, selectedValue, onChange }) => {
    const { t } = useTranslation();
    return (
        <FormControlLabel
            value={value}
            control={<Radio checked={selectedValue === value} onChange={onChange} />}
            label={t(value)}
            sx={{ margin: 0, pr: 2 }}
        />
    );
};

const ASSET_ALL = "ALL";
const ASSET_CARS = ASSET_TYPE_CAR;
const ASSET_SPACES = ASSET_TYPE_PLACE;

const GroupAssignment = ({
    assets,
    updatedGroup,
    handleAddAsset,
    handleRemoveAsset,
}) => {

    const { t, i18n } = useTranslation();
    const { spacing } = useTheme();
    const [dateFrom, setDateFrom] = useState(dayjs().add(1, 'day').set('hour', 9).set('m', 0));
    const [dateTo, setDateTo] = useState(null);
    const [error, setError] = useState({});
    const [filter, setFilter] = useState(ASSET_ALL);
    const processedAssets = useMemo(() => {
        // Added assigned boolean to assets for easier table rander
        return assets.filter(a => filter === ASSET_ALL || a.type === filter).map(a => {
            const assetAssignment = updatedGroup?.assets?.find(a2 => a2.assetId === a.assetId);
            let res = { ...a, assigned: !!assetAssignment };
            if (assetAssignment) {
                res.dateFrom = assetAssignment.dateFrom;
                res.dateTo = assetAssignment.dateTo;
            }
            return res;
        });
    }, [assets, updatedGroup, filter])

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    const validate = () => {
        let ret = true;
        let error = {};
        if (!dateFrom) {
            error.dateFrom = t('THIS_FIELD_IS_REQUIRED');
            ret = false;
        }
        if (!dateTo) {
            error.dateTo = t('THIS_FIELD_IS_REQUIRED');
            ret = false;
        }
        if (dateTo && dateFrom) {
            if (dateTo.isBefore(dateFrom)) {
                error.dateTo = t('DATE_FROM_CANNOT_BE_AFTER_DATE_TO');
                ret = false;
            }

        }

        setError(error);
        return ret;
    }

    const handleAssetAssignmentChange = (asset) => {
        console.log("handleAssetAssignmentChange", asset);
        const assetGroup = { assetId: asset.assetId, dateFrom: dateFrom, dateTo: dateTo };
        if (asset.assigned) {
            handleRemoveAsset(assetGroup);
        } else {
            if (validate()) {
                handleAddAsset(assetGroup);
            }
        }
    }

    const handleAssetAssignmentChangeAll = () => {
        const assignedAll = processedAssets.reduce((agg, pa) => agg && pa.assigned, true);
        processedAssets.forEach(asset => {
            if (asset.assigned === assignedAll) {
                console.log("Setting asset ", asset, assignedAll);
                handleAssetAssignmentChange(asset);
            }
        });
    }

    const cellStyle = {
        paddingLeft: spacing(2),
        paddingRight: spacing(2),
        color: (theme) => theme.palette.background.contrastText,
    };
    const headerStyle = {
        paddingLeft: spacing(2),
        paddingRight: spacing(2),
        color: (theme) => theme.palette.background.contrastText,
    }

    const columns = [
        {
            title: <Checkbox
                checked={processedAssets.reduce((agg, pa) => agg && pa.assigned, true)}
                onChange={() => handleAssetAssignmentChangeAll()}
            />,
            field: 'assigned',
            cellStyle: {
                ...cellStyle,
            },
            headerStyle: {
                ...headerStyle
            },
            width: "1%",
            render: (rowData) => <Checkbox
                checked={rowData.assigned}
                onChange={() => handleAssetAssignmentChange(rowData)}
            />,
            sorting: false,
        },
        {
            title: t('ASSET_NAME'),
            field: 'name',
            cellStyle: {
                ...cellStyle,
            },
            headerStyle: {
                ...headerStyle
            },
            width: "25%",
            render: (rowData) => <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                verticalAlign: 'middle'
            }}>
                {rowData.type === ASSET_TYPE_CAR ? <DirectionsCarIcon color='primary' /> : <LocalParkingIcon color='primary' />}
                {rowData.name}
            </Box>
        },
        {
            title: t('ASSET_LOCATION'),
            field: 'locationName',
            cellStyle: {
                ...cellStyle,
            },
            headerStyle: {
                ...headerStyle
            },
            width: "25%",
            render: (rowData) => (
                rowData.locationName
            ),
        },
        {
            title: t('ASSET_DATE_FROM'),
            field: 'dateFrom',
            cellStyle: {
                ...cellStyle,
            },
            headerStyle: {
                ...headerStyle
            },
            width: "25%",
            render: (rowData) => (
                !!rowData.dateFrom ? dayjsToDateTimeString(dayjs(rowData.dateFrom), i18n.language) : t("N_A")
            )
        },
        {
            title: t('ASSET_DATE_TO'),
            field: 'dateTo',
            cellStyle: {
                ...cellStyle,
            },
            headerStyle: {
                ...headerStyle
            },
            width: "25%",
            render: (rowData) => (
                !!rowData.dateTo ? dayjsToDateTimeString(dayjs(rowData.dateTo), i18n.language) : t("N_A")
            )
        },
    ];

    return (
        <Grid container>
            <Grid item xs={12} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {t('GROUP_BULK_ASSIGNMENT')}
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h7" sx={{ padding: 2 }}>
                            {t("FROM")}
                        </Typography>
                        <CustomDateTimePicker
                            dateTime={dateFrom}
                            setDateTime={setDateFrom}
                            error={error.dateFrom}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h7" sx={{ padding: 2 }}>
                            {t("TO")}
                        </Typography>
                        <CustomDateTimePicker
                            dateTime={dateTo}
                            setDateTime={setDateTo}
                            error={error.dateTo}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            {t('ASSETS')}
                        </Typography>
                        <RadioGroup
                            row
                            value={filter}
                            onChange={handleFilterChange}
                            sx={{ mb: 1 }}
                        >
                            <FilterRadioButton
                                key={ASSET_ALL}
                                value={ASSET_ALL}
                                selectedValue={filter}
                                onChange={handleFilterChange}
                            />
                            <FilterRadioButton
                                key={ASSET_CARS}
                                value={ASSET_CARS}
                                selectedValue={filter}
                                onChange={handleFilterChange}
                            />
                            <FilterRadioButton
                                key={ASSET_SPACES}
                                value={ASSET_SPACES}
                                selectedValue={filter}
                                onChange={handleFilterChange}
                            />
                        </RadioGroup>

                        <CustomTable
                            title=""
                            columns={columns}
                            data={processedAssets}
                            options={{
                                headerStyle: {
                                    padding: '15px 8px 15px 34px',
                                    lineHeight: '12px',
                                    color: (theme) => theme.palette.background.contrastText,
                                    textAlign: 'center',
                                },
                                paging: false,
                            }}
                            isLoading={processedAssets ? false : true}
                            loadAllData={false}
                            onRowClick={(event, rowData) => handleAssetAssignmentChange(rowData)}
                        />

                        {/* <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={processedAssets.reduce((agg, pa) => agg && pa.assigned, true)}
                                                onChange={() => handleAssetAssignmentChangeAll()}
                                            />
                                        </TableCell>
                                        <TableCell>{t('ASSET_NAME')}</TableCell>
                                        <TableCell>{t('ASSET_LOCATION')}</TableCell>
                                        <TableCell>{t('ASSET_DATE_FROM')}</TableCell>
                                        <TableCell>{t('ASSET_DATE_TO')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processedAssets.map((asset) => (
                                        <TableRow
                                            key={asset.assetId}
                                            hover
                                            onClick={() => handleAssetAssignmentChange(asset)}
                                            selected={asset.assigned}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={asset.assigned}
                                                    onChange={() => handleAssetAssignmentChange(asset)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    verticalAlign: 'middle'
                                                }}>
                                                    {asset.type === ASSET_TYPE_CAR ? <DirectionsCarIcon color='primary' /> : <LocalParkingIcon color='primary' />}
                                                    {asset.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{asset.locationName}</TableCell>
                                            <TableCell>{!!asset.dateFrom ? dayjsToDateTimeString(dayjs(asset.dateFrom), i18n.language) : t("N_A")}</TableCell>
                                            <TableCell>{!!asset.dateTo ? dayjsToDateTimeString(dayjs(asset.dateTo), i18n.language) : t("N_A")}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer> */}
                    </Grid>


                </Grid>
            </Grid>
        </Grid>
    );
};

export default GroupAssignment;